<template>
 <div class="lesson-signups-tab">
 <DeparturesToCall />
 <!-- Header -->
 <div class="mb-4">
 <div class="flex items-center gap-3">
 <input
 v-model="search"
 type="search"
 placeholder="Search…"
 class="flex-1 min-w-0 text-xs px-2 py-0.5 rounded-md border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-base dark:text-fg-base-dark focus:outline-none focus:ring-2 focus:ring-blue-500"
 />
 <select
 v-model="filter"
 :disabled="!!searchTerm"
 class="text-xs px-1.5 py-0.5 rounded-md border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-dark text-fg-secondary dark:text-fg-secondary-dark focus:outline-none focus:ring-2 focus:ring-blue-500"
 >
 <option value="open">Open</option>
 <option value="waitlist">Waitlist</option>
 <option v-for="c in closedCategories" :key="c.key" :value="c.key">{{ c.label }} ({{ c.count }})</option>
 <option v-if="hiddenCount > 0 || filter === 'all'" value="all">All</option>
 </select>
 <button
 @click="refresh"
 :disabled="isLoading"
 class="text-fg-faint dark:text-fg-secondary-dark"
 >
 <ArrowPathIcon :class="['h-3.5 w-3.5', isLoading ? 'animate-spin' : '']" />
 </button>
 </div>
 </div>

 <!-- The "Notify me" waitlist (plan 64) replaces the waitlisted cards in this filter -->
 <WaitlistPanel v-if="filter === 'waitlist' && !searchTerm" :is-admin="isAdmin" />

 <!-- Loading skeleton -->
 <div v-else-if="isLoading && inquiries.length === 0" class="card text-center py-12">
 <div class="animate-pulse">
 <div class="h-4 w-48 bg-stroke dark:bg-stroke-dark rounded mx-auto mb-4"></div>
 <div class="h-3 w-32 bg-stroke dark:bg-stroke-dark rounded mx-auto"></div>
 </div>
 </div>

 <!-- Error -->
 <div v-else-if="error" class="card border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
 <div class="flex items-center">
 <ExclamationTriangleIcon class="h-5 w-5 text-red-500 dark:text-red-400 mr-3 flex-shrink-0" />
 <div>
 <p class="font-medium text-red-800 dark:text-red-300">Failed to load signups</p>
 <p class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>
 </div>
 <button @click="refresh" class="ml-auto text-sm px-3 py-1.5 rounded-md border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-dark text-fg-secondary dark:text-fg-secondary-dark">Retry</button>
 </div>
 </div>

 <!-- Empty -->
 <div v-else-if="visibleInquiries.length === 0" class="card text-center py-12">
 <AllCaughtUp v-if="filter === 'open' && inquiries.length > 0 && !searchTerm" />
 <template v-else>
 <UserPlusIcon class="h-12 w-12 mx-auto text-stroke-strong dark:text-stroke-strong-dark mb-3" />
 <p class="text-fg-muted dark:text-fg-muted-dark">
 {{ inquiries.length === 0 ? 'No lesson signups yet' : searchTerm ? 'No signups match this search' : 'No signups match this filter' }}
 </p>
 </template>
 <p v-if="filter === 'open' && hiddenCount > 0 && !searchTerm" class="text-sm text-fg-faint dark:text-fg-faint-dark mt-1">
 <button @click="filter = 'all'" class="underline">Show {{ hiddenCount }} closed</button>
 </p>
 </div>

 <!-- Cards -->
 <div v-else class="space-y-3">
 <SignupCard
 v-for="inquiry in visibleInquiries"
 :key="inquiry.id"
 :inquiry="inquiry"
 :is-admin="isAdmin"
 @updated="replaceInquiry"
 @busy="b => busyCount += b ? 1 : -1"
 />

 </div>

 <!-- Rehearsal Requests (admin only) -->
 <template v-if="isAdmin && pendingRehearsals.length > 0">
 <div class="mt-6 pt-4 border-t border-stroke dark:border-stroke-dark">
 <div class="flex items-center justify-between mb-3">
 <h3 class="text-xs font-semibold text-fg-faint dark:text-fg-faint-dark uppercase tracking-widest">Rehearsal Requests</h3>
 <span class="text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark">{{ pendingRehearsals.length }}</span>
 </div>
 <div class="space-y-3">
 <div v-for="b in pendingRehearsals" :key="b.id" class="card">
 <div class="flex items-start gap-3">
 <div
 class="h-14 w-14 rounded-full flex-shrink-0 flex items-center justify-center text-white text-lg font-semibold"
 :style="{ background: avatarGradient(b.customer_name) }"
 >{{ initials(b.customer_name) }}</div>
 <div class="flex-1 min-w-0">
 <div class="flex justify-between items-start gap-2">
 <span class="font-semibold text-fg-base dark:text-fg-base-dark text-sm leading-tight">{{ b.customer_name }}</span>
 <span class="text-xs text-fg-faint dark:text-fg-faint-dark tabular-nums flex-shrink-0">{{ elapsedTime(b.created_at) }}</span>
 </div>
 <p class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5">{{ b.room_name }} · {{ fmtSlotDate(b.booking_date) }} · {{ fmtSlotTime(b.start_time) }}–{{ fmtSlotTime(b.end_time) }}</p>
 <p class="text-xs text-fg-muted dark:text-fg-muted-dark">{{ b.num_people }} {{ b.num_people === 1 ? 'person' : 'people' }} · ${{ (b.amount_cents / 100).toFixed(2) }}</p>
 </div>
 </div>
 <p v-if="b.notes" class="mt-2 text-xs text-fg-secondary dark:text-fg-secondary-dark whitespace-pre-line">{{ b.notes }}</p>
 <div class="mt-2.5 flex items-center justify-between gap-3">
 <div class="flex flex-col gap-0.5 min-w-0">
 <a :href="`mailto:${b.customer_email}`" @click.stop class="text-xs text-fg-muted dark:text-fg-muted-dark truncate">{{ b.customer_email }}</a>
 <span v-if="b.customer_phone" class="text-xs text-fg-muted dark:text-fg-muted-dark tabular-nums">{{ formatPhone(b.customer_phone) }}</span>
 </div>
 <div class="flex-shrink-0 flex items-center gap-1.5">
 <button
 @click="handleRejectRehearsal(b)"
 :disabled="rehearsalPending === b.id"
 class="action-btn-ghost"
 >{{ rehearsalRejectKey === b.id ? 'Confirm?' : rehearsalPending === b.id ? '…' : 'Reject' }}</button>
 <button
 @click="approveRehearsal(b)"
 :disabled="rehearsalPending === b.id"
 class="action-btn-primary"
 >{{ rehearsalPending === b.id ? '…' : 'Approve' }}</button>
 </div>
 </div>
 </div>
 </div>
 </div>
 </template>


 </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import {
 ArrowPathIcon,
 ExclamationTriangleIcon,
 UserPlusIcon,
} from '@heroicons/vue/24/outline'
import { useApi } from '../../composables/useApi.js'
import { cardAction, isClosed } from '../../composables/useSubmissionStage.js'
import { avatarGradient, initials } from '../../composables/useAvatarGradient.js'
import { useNowTicker, elapsedTime, fmtSlotDate, fmtSlotTime, formatPhone } from '../../composables/useSignupFormat.js'
import SignupCard from '../common/SignupCard.vue'
import AllCaughtUp from '../common/AllCaughtUp.vue'
import DeparturesToCall from '../common/DeparturesToCall.vue'
import WaitlistPanel from '../common/WaitlistPanel.vue'

const props = defineProps({
 isAdmin: { type: Boolean, default: false },
})

const { fetchApi } = useApi()

const inquiries = ref([])
const isLoading = ref(false)
const error = ref(null)
const filter = ref('open') // 'open' | 'all' | a closedCategory key
const busyCount = ref(0) // cards with a request in flight — poll waits for them
let pollInterval = null
useNowTicker()

const replaceInquiry = (updated) => {
 const idx = inquiries.value.findIndex(r => r.id === updated.id)
 if (idx !== -1) inquiries.value[idx] = updated
}

// ── Rehearsal requests (admin only) ───────────────────────────────────────────
const rehearsalBookings = ref([])
const rehearsalPending = ref(null)
const rehearsalRejectKey = ref(null)
let rehearsalRejectTimer = null

const pendingRehearsals = computed(() =>
 rehearsalBookings.value.filter(b => b.status === 'pending')
)

const hiddenCount = computed(() => inquiries.value.filter(isClosed).length)

// Why a closed card is hidden. A closed card with follow_up_at is always a
// snooze — the backend never closes one whose follow-up date has arrived.
const CLOSED_LABELS = {
 snoozed: 'Snoozed', converted: 'Converted', waitlist: 'Waitlist',
 lost: 'Lost', declined: 'Declined', cancelled: 'Cancelled', other: 'Other closed',
}
const closedCategory = (row) => {
 if (row.follow_up_at) return 'snoozed'
 const key = (row.stage || '').toLowerCase()
 if (key in CLOSED_LABELS) return key
 return row.status in CLOSED_LABELS ? row.status : 'other'
}

const closedCategories = computed(() => {
 const counts = {}
 for (const r of inquiries.value) {
 if (isClosed(r)) counts[closedCategory(r)] = (counts[closedCategory(r)] || 0) + 1
 }
 return Object.entries(CLOSED_LABELS)
 .filter(([key]) => key !== 'waitlist' && (counts[key] || filter.value === key))
 .map(([key, label]) => ({ key, label, count: counts[key] || 0 }))
})

const _earliestSlotMs = (row) => {
 const slots = (row.booking_data || []).filter(s => !s._cleared)
 if (!slots.length) return null
 let earliest = Infinity
 for (const s of slots) {
 if (!s.date) continue
 const [y, mo, d] = s.date.split('-').map(Number)
 const [h, m] = (s.time || '23:59').split(':').map(Number)
 const ms = new Date(y, mo - 1, d, h, m).getTime()
 if (ms < earliest) earliest = ms
 }
 return earliest === Infinity ? null : earliest
}

const _sortInquiries = (rows) => [...rows].sort((a, b) => {
 const aSlot = _earliestSlotMs(a)
 const bSlot = _earliestSlotMs(b)

 // No slot always sorts first; tiebreak by created_at
 if (aSlot === null && bSlot === null) return new Date(a.created_at) - new Date(b.created_at)
 if (aSlot === null) return -1
 if (bSlot === null) return 1

 // Both have slots: admin-action cards before non-admin
 const aAdmin = cardAction(a) !== null ? 0 : 1
 const bAdmin = cardAction(b) !== null ? 0 : 1
 if (aAdmin !== bAdmin) return aAdmin - bAdmin

 return aSlot - bSlot
})

// Search covers every signup, closed ones included (it overrides the filter), so a
// lost or waitlisted family can be found and reopened without knowing where it went.
const search = ref('')
const searchTerm = computed(() => search.value.trim().toLowerCase())
const _matchesSearch = (row, term) => {
 const digits = term.replace(/\D/g, '')
 return [row.name, row.student_name, row.email, row.instrument].some(v => (v || '').toLowerCase().includes(term))
 || (digits.length >= 3 && (row.phone || '').replace(/\D/g, '').includes(digits))
}

const visibleInquiries = computed(() => {
 if (searchTerm.value) return _sortInquiries(inquiries.value.filter(r => _matchesSearch(r, searchTerm.value)))
 const f = filter.value
 const rows = f === 'all' ? inquiries.value
 : f === 'open' ? inquiries.value.filter(r => !isClosed(r))
 : inquiries.value.filter(r => isClosed(r) && closedCategory(r) === f)
 return _sortInquiries(rows)
})

// ── Rehearsal approve / reject ─────────────────────────────────────────────────
const fetchRehearsals = async () => {
 try {
 rehearsalBookings.value = await fetchApi('/api/admin/rehearsal-bookings')
 } catch {}
}

const approveRehearsal = async (booking) => {
 if (rehearsalPending.value) return
 rehearsalPending.value = booking.id
 try {
 const updated = await fetchApi(`/api/admin/rehearsal-bookings/${booking.id}/approve`, { method: 'POST' })
 const idx = rehearsalBookings.value.findIndex(b => b.id === booking.id)
 if (idx !== -1) rehearsalBookings.value[idx] = updated
 } catch (e) {
 console.error('Failed to approve rehearsal booking', e)
 } finally {
 rehearsalPending.value = null
 }
}

const handleRejectRehearsal = (booking) => {
 if (rehearsalRejectKey.value === booking.id) {
 rejectRehearsal(booking)
 return
 }
 rehearsalRejectKey.value = booking.id
 if (rehearsalRejectTimer) clearTimeout(rehearsalRejectTimer)
 rehearsalRejectTimer = setTimeout(() => { rehearsalRejectKey.value = null }, 3000)
}

const rejectRehearsal = async (booking) => {
 if (rehearsalRejectTimer) clearTimeout(rehearsalRejectTimer)
 rehearsalRejectKey.value = null
 if (rehearsalPending.value) return
 rehearsalPending.value = booking.id
 try {
 const updated = await fetchApi(`/api/admin/rehearsal-bookings/${booking.id}/reject`, { method: 'POST' })
 const idx = rehearsalBookings.value.findIndex(b => b.id === booking.id)
 if (idx !== -1) rehearsalBookings.value[idx] = updated
 } catch (e) {
 console.error('Failed to reject rehearsal booking', e)
 } finally {
 rehearsalPending.value = null
 }
}

// ── Data fetching ──────────────────────────────────────────────────────────────
const refresh = async () => {
 if (isLoading.value) return
 isLoading.value = true
 error.value = null
 try {
 // Keep unchanged rows' object identity so a poll only re-renders the cards
 // whose data actually changed.
 const prev = new Map(inquiries.value.map(r => [r.id, r]))
 const fresh = (await fetchApi('/api/admin/submissions')).map(r => {
 const old = prev.get(r.id)
 return old && JSON.stringify(old) === JSON.stringify(r) ? old : r
 })
 if (fresh.length !== inquiries.value.length || fresh.some((r, i) => r !== inquiries.value[i])) {
 inquiries.value = fresh
 }
 } catch (err) {
 error.value = err.message || 'Unknown error'
 } finally {
 isLoading.value = false
 }
}

// ── Lifecycle ──────────────────────────────────────────────────────────────────
onMounted(() => {
 refresh()
 pollInterval = setInterval(() => {
 if (!busyCount.value) refresh()
 if (!rehearsalPending.value && props.isAdmin) fetchRehearsals()
 }, 10_000)
 if (props.isAdmin) {
 fetchRehearsals()
 }
})

onUnmounted(() => {
 if (pollInterval) clearInterval(pollInterval)
 if (rehearsalRejectTimer) clearTimeout(rehearsalRejectTimer)
})
</script>

<style scoped>
.lesson-signups-tab {
 @apply pb-6;
}
</style>
